import { zodResolver } from '@hookform/resolvers/zod'
import { AwardType, TenantMemberOrTeamDisplayData } from '@kudos/http-client'
import { createMentionsTextAreaFieldSchema } from '@shared/forms/mentionsTextareaField/MentionsTextareaField'
import { TFunction } from '@shared/types/types'
import stripHtml from '@shared/utils/stripHtml'
import { useT } from '@transifex/react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import useSendAwardMutations from './useSendAwardMutations'

const createSendAwardFormSchema = (t: TFunction) =>
  z.object({
    amount: z.preprocess(
      value => (typeof value === 'string' ? parseInt(value, 10) : value),
      z
        .number({
          message: t('Points required'),
        })
        .min(0, { message: t('Points must be a positive number') }),
    ),
    message: createMentionsTextAreaFieldSchema(t),
    sendTo: z
      .array(z.custom<TenantMemberOrTeamDisplayData>())
      .min(1, { message: t('Select a recipient') }),
  })

export type SendAwardFormData = z.infer<ReturnType<typeof createSendAwardFormSchema>>

const useSendAwardForm = (awardType: AwardType) => {
  const t = useT()
  const { id: awardTypeId, points } = awardType
  const SendAwardFormSchema = createSendAwardFormSchema(t)

  const defaults: SendAwardFormData = {
    amount: points,
    message: '',
    sendTo: [],
  }

  const formMethods = useForm<SendAwardFormData>({
    resolver: zodResolver(SendAwardFormSchema),
    defaultValues: defaults,
  })

  const { reset, setError, formState, control } = formMethods
  const { publishAward } = useSendAwardMutations(awardTypeId, setError)
  const sendTo = useWatch({ control, name: 'sendTo' })
  const message = useWatch({ control, name: 'message' })
  const isFormDirty = formState.isDirty && (sendTo.length > 0 || stripHtml(message).length > 0)

  const handleResetAwardForm = () => {
    reset(defaults)
  }

  return {
    formMethods,
    publishAward,
    handleResetAwardForm,
    isFormDirty,
  }
}

export default useSendAwardForm
